import axios from 'axios';
import { getAccessToken, getRefreshToken, setTokens } from '../../utils/tokenStorage';
import { clearAuth } from '../../utils/auth';

// API base URL - using the provided base URL
const API_BASE_URL = 'https://beta-auth.wdtp.io/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Enable request debugging in development
const DEBUG_API = true;

// Request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Debug logging for requests
    if (DEBUG_API) {
      const requestData = config.data ? JSON.parse(JSON.stringify(config.data)) : {};
      
      // Don't log sensitive data like passwords
      if (requestData.password) {
        requestData.password = '******';
      }
      
      console.log(`🚀 Request: ${config.method.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: requestData,
        params: config.params
      });
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and debug responses
api.interceptors.response.use(
  (response) => {
    // Debug logging for successful responses
    if (DEBUG_API) {
      console.log(`✅ Response: ${response.status} ${response.config.method.toUpperCase()} ${response.config.url}`, {
        data: response.data
      });
    }
    return response;
  },
  async (error) => {
    // Debug logging for error responses
    if (DEBUG_API && error.response) {
      console.error(`❌ Error Response: ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        data: error.response.data
      });
    }
    
    const originalRequest = error.config;
    
    // If error is 401 Unauthorized and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const refreshToken = getRefreshToken();
        
        if (!refreshToken) {
          // No refresh token available, logout user
          clearAuth();
          // Don't immediately redirect - let the component handle that
          return Promise.reject(error);
        }
        
        console.log('Attempting to refresh token...');
        
        // Check if the application has a variable indicating refresh token is not supported
        // This could be set after a 404 error on the refresh endpoint
        if (window.REFRESH_TOKEN_NOT_SUPPORTED) {
          console.log('Token refresh not supported by this API. Clearing auth and rejecting.');
          clearAuth();
          return Promise.reject(error);
        }
        
        // Call token refresh endpoint - updated with /auth/ prefix
        const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken,
        }).catch(refreshError => {
          // If we get 404, mark that refresh token endpoint doesn't exist
          if (refreshError.response && refreshError.response.status === 404) {
            console.warn('Refresh token endpoint returned 404. API may not support token refresh.');
            window.REFRESH_TOKEN_NOT_SUPPORTED = true;
          }
          throw refreshError;
        });
        
        // Set new tokens
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        setTokens(accessToken, newRefreshToken);
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        // If refresh token is invalid or expired, logout user
        clearAuth();
        // Don't immediately redirect - let the component handle that
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api; 